// src/utilities/useBattery.ts
import { computed, Ref } from "@vue/composition-api";

type BatteryLevel = 0 | 1 | 2 | 3 | 4 | 5;

const ICONS: Record<BatteryLevel, string> = {
    0: require("@/assets/icons/battery-0.svg"),
    1: require("@/assets/icons/battery-1.svg"),
    2: require("@/assets/icons/battery-2.svg"),
    3: require("@/assets/icons/battery-3.svg"),
    4: require("@/assets/icons/battery-4.svg"),
    5: require("@/assets/icons/battery-5.svg"),
};

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}

/** Accepts either 0�5 level or 0�100 percent. Returns 0�5 or null if unknown. */
export function normalizeBattery(raw: number | null | undefined): BatteryLevel | null {
    if (raw == null || Number.isNaN(Number(raw))) return null;

    if (raw <= 5) {
        return clamp(Math.floor(raw), 0, 5) as BatteryLevel;
    }

    const p = clamp(Math.floor(raw), 0, 100);
    if (p >= 83) return 5;
    if (p >= 66) return 4;
    if (p >= 50) return 3;
    if (p >= 33) return 2;
    if (p >= 16) return 1;
    return 0;
}

export function iconForLevel(level: BatteryLevel | null): string | null {
    return level == null ? null : ICONS[level];
}

/** Composable: pass a ref to the raw battery (percent or level). */
export function useBattery(rawRef: Ref<number | null | undefined>) {
    const level = computed<BatteryLevel | null>(() => normalizeBattery(rawRef.value));
    const icon = computed<string | null>(() => iconForLevel(level.value));
    const label = computed<string>(() => (level.value == null ? "N/A" : `${level.value}/5`));
    return { level, icon, label };
}